<script lang="ts">
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Plus, Trash2, X, Layers } from 'lucide-svelte';
  import { adminVerticalsApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { Vertical } from '$lib/types';
  import { ordersEnabled, feedbackRoutingPayload } from '$lib/utils/orders';
  import { fetchMe } from '$lib/api/me';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  type Routing = { bangalore: { vertical_id?: number; assignee_user_id?: number }; outside: { vertical_id?: number; assignee_user_id?: number } };

  const queryClient = useQueryClient();

  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let formError = $state('');
  let newStage = $state('');
  let newCategory = $state('');

  const defaultColor = '#226A73';
  function color(value: unknown): string {
    return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : defaultColor;
  }
  function colorMap(value: unknown): Record<string, string> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(Object.entries(value).map(([key, value]) => [key, color(value)]));
  }

  function emptyForm() {
    return { name: '', slug: '', color: defaultColor, stage_colors: {} as Record<string, string>, category_colors: {} as Record<string, string>, settings: {} as Record<string, unknown>, stages: [] as string[], orders_enabled: false, routing: { bangalore: {}, outside: {} } as Routing };
  }
  let form = $state(emptyForm());

  const verticalsQuery = createQuery(() => ({
    queryKey: ['admin-verticals'],
    queryFn: () => adminVerticalsApi.list()
  }));

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? adminVerticalsApi.update(editingId, body) : adminVerticalsApi.create(body),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['admin-verticals'] });
      closeForm();
      try { session.setMe(await fetchMe()); } catch { toast.error('Settings saved, but session settings could not refresh. Reload to see the new settings.'); }
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => adminVerticalsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-verticals'] })
  }));

  function openCreate() {
    editingId = null;
    form = emptyForm();
    formError = '';
    showForm = true;
  }

  function openEdit(row: Vertical) {
    editingId = row.id;
    const settings = row.settings ?? {};
    form = {
      name: row.name,
      slug: row.slug,
      color: color(settings.color),
      stage_colors: colorMap(settings.stage_colors),
      category_colors: colorMap(settings.category_colors),
      settings,
      orders_enabled: ordersEnabled(row),
      routing: { bangalore: { ...(settings.order_feedback_routing as Routing | undefined)?.bangalore }, outside: { ...(settings.order_feedback_routing as Routing | undefined)?.outside } },
      stages: Array.isArray(settings.deal_stages) ? [...(settings.deal_stages as string[])] : []
    };
    formError = '';
    showForm = true;
  }

  function closeForm() {
    showForm = false;
  }

  function addStage() {
    const s = newStage.trim();
    if (s && !form.stages.includes(s)) { form.stages.push(s); form.stage_colors[s] = defaultColor; }
    newStage = '';
  }

  function removeStage(s: string) {
    form.stages = form.stages.filter((x) => x !== s);
    delete form.stage_colors[s];
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) {
      formError = 'Name and slug are required';
      return;
    }
    formError = '';
    let routing;
    try { routing = feedbackRoutingPayload(form.routing); }
    catch (error) { formError = getErrorMessage(error); return; }
    saveMutation.mutate({
      name: form.name,
      slug: form.slug,
      settings: { ...form.settings, color: form.color, stage_colors: form.stage_colors, category_colors: form.category_colors, deal_stages: form.stages, orders_enabled: form.orders_enabled, order_feedback_routing: routing }
    });
  }

  function remove(row: Vertical) {
    scheduleUndo(`Delete vertical "${row.name}" (data retained)`, () => deleteMutation.mutate(row.id));
  }
</script>

<div>
  <div class="mb-[18px] flex items-center">
    <div>
      <h2 class="tym-section-title">Verticals</h2>
      <div class="tym-section-sub">Data partitions &middot; create &amp; rename</div>
    </div>
    <span class="flex-1"></span>
    <button type="button" class="btn-primary" onclick={openCreate}>
      <Plus class="h-4 w-4" /> New vertical
    </button>
  </div>

  {#if verticalsQuery.isLoading}
    <p class="tym-loading-state">Loading verticals&hellip;</p>
  {:else if verticalsQuery.isError}
    <p class="tym-error-state">{getErrorMessage(verticalsQuery.error)}</p>
  {:else if verticalsQuery.data}
    <div class="flex max-w-[560px] flex-col gap-2.5">
      {#each verticalsQuery.data.results as row (row.id)}
        <div class="tym-card flex items-center gap-[13px] px-[18px] py-[15px]">
          <span
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px]"
            style="background:{color(row.settings?.color)}22; color:{color(row.settings?.color)}"
          >
            <Layers class="h-5 w-5" />
          </span>
          <div class="min-w-0 flex-1">
            <div class="font-heading text-[14.5px] font-bold text-navy">{row.name}</div>
            <div class="truncate text-xs text-dark-gray">
              {row.slug}
              {#if (row.settings?.deal_stages as string[] | undefined)?.length}
                &middot; {(row.settings?.deal_stages as string[]).join(', ')}
              {/if}
            </div>
          </div>
          <button type="button" class="btn-secondary !h-9 shrink-0" onclick={() => openEdit(row)}>Rename</button>
          <button
            type="button"
            class="btn-danger !h-10 !w-10 shrink-0 !p-0"
            onclick={() => remove(row)}
            aria-label={`Delete ${row.name}`}
          >
            <Trash2 class="h-4 w-4" />
          </button>
        </div>
      {:else}
        <p class="tym-empty-state">No verticals found.</p>
      {/each}
    </div>
  {/if}
</div>

{#if showForm}
  <Modal title={editingId ? 'Edit vertical' : 'New vertical'} onClose={closeForm}>
    <form class="space-y-4" onsubmit={submit}>
      <div>
        <label class="label" for="name">Name</label>
        <input id="name" class="input" bind:value={form.name} />
      </div>
      <div>
        <label class="label" for="slug">Slug</label>
        <input id="slug" class="input" bind:value={form.slug} disabled={!!editingId} />
      </div>
      <label class="label" for="vertical-color">Vertical colour</label>
      <input id="vertical-color" type="color" bind:value={form.color} class="h-11 w-16" />
      <div>
        <span class="label">Deal stages</span>
        <label class="mb-4 block"><input type="checkbox" bind:checked={form.orders_enabled} /> Enable order booking and verified feedback</label>
        <fieldset class="mb-4 space-y-3"><legend class="label">Not-fine feedback routing (IDs, not display names)</legend>
          {#each ['bangalore', 'outside'] as region}
            {@const key = region as keyof Routing}
            <div class="space-y-2"><p class="font-bold capitalize">{region}</p>
              <label class="label" for="route-team-{region}">Destination vertical</label>
              <select id="route-team-{region}" class="input" bind:value={form.routing[key].vertical_id}><option value={undefined}>Not configured</option>{#each verticalsQuery.data?.results ?? [] as v (v.id)}<option value={v.id}>{v.name} (ID {v.id})</option>{/each}</select>
              <label class="label" for="route-owner-{region}">Assignee user ID</label><input id="route-owner-{region}" class="input" type="number" min="1" step="1" bind:value={form.routing[key].assignee_user_id} />
            </div>
          {/each}
        </fieldset>
        <div class="flex flex-wrap gap-2 mb-2">
          {#each form.stages as s (s)}
            <span class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
              {s}
              <input type="color" aria-label={`Colour for stage ${s}`} value={form.stage_colors[s] ?? defaultColor} oninput={e => { form.stage_colors[s] = e.currentTarget.value; }} class="h-7 w-8" />
              <button type="button" onclick={() => removeStage(s)} aria-label={`Remove ${s}`}>
                <X class="h-3 w-3" />
              </button>
            </span>
          {/each}
        </div>
        <div class="flex gap-2">
          <input
            class="input"
            placeholder="Add a stage"
            bind:value={newStage}
            onkeydown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addStage();
              }
            }}
          />
          <button type="button" class="btn-secondary" onclick={addStage}>Add</button>
        </div>
      </div>

      <fieldset class="space-y-2">
        <legend class="label">Category colours</legend>
        {#each Object.keys(form.category_colors) as category (category)}
          <div class="flex items-center gap-3">
            <span class="flex-1 text-sm">{category}</span>
            <input type="color" aria-label={`Colour for category ${category}`} bind:value={form.category_colors[category]} class="h-9 w-12" />
            <button type="button" class="btn-secondary" aria-label={`Remove colour for ${category}`} onclick={() => { delete form.category_colors[category]; }}>Remove</button>
          </div>
        {/each}
        <div class="flex gap-2"><input class="input" aria-label="Category name" placeholder="Category name" bind:value={newCategory} /><button type="button" class="btn-secondary" onclick={() => { const category = newCategory.trim(); if (category) form.category_colors[category] = defaultColor; newCategory = ''; }}>Add colour</button></div>
      </fieldset>

      {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}

      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
        <button type="submit" class="btn-primary" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  </Modal>
{/if}
